import React, { Component } from 'react';
import { func, array, string, bool } from 'prop-types';
import { connect } from 'react-redux';
import { translate, Trans } from 'react-i18next';
import classnames from 'classnames/bind';

import { Button } from '@snapask/common-components';
import { setCancelSubscriptionInfo } from '@dragonfruit/redux/cancelSubscription';
import { goToAccountSubscriptionCancelPage } from '@dragonfruit/redux/navigation';
import ChangeSubscriptionModal from '../ChangeSubscriptionModal';

import { yearMonthDayFormat } from '@dragonfruit/utils/dateParser';
import { isNeverExpired } from '@dragonfruit/utils/checkPlanExpire';
import { APPLE, GOOGLE, BRAINTREE } from '@dragonfruit/constants/paymentTypes';
import {
  MANAGEABLE,
  PAUSED,
  CONTRACT_PLAN,
  REPLACEMENT,
  RETIRED,
  CANCELLED,
  APPLIED_FOR_CANCELLATION,
} from '@dragonfruit/constants/subscriptionManagementStatus';
import { DRAGON_FRUIT_CANCEL } from '@dragonfruit/constants/zendeskProjectTypes';

import MoreIcon from '@dragonfruit/images/icons/more.svg';
import styles from './AccountCurrentSubscriptions.scss';
const cx = classnames.bind(styles);

const notAllowModifyOnWeb = {
  [APPLE]: 'account.planStatus.notAllowModify.ios',
  [GOOGLE]: 'account.planStatus.notAllowModify.android',
};

const mapStateToProps = state => ({
  isReportFormOpen: state.report.isReportFormOpen,
  canCancelSubscription: state.auth.uiSetting.canCancelSubscription,
});

class AccountCurrentSubscriptions extends Component {
  static propTypes = {
    canCancelSubscription: bool.isRequired,
    goToAccountSubscriptionCancelPage: func.isRequired,
    isReportFormOpen: bool.isRequired,
    setCancelSubscriptionInfo: func.isRequired,
    setReportOptions: func.isRequired,
    t: func.isRequired,
    toggleReportForm: func.isRequired,
    onClickChangePlan: func.isRequired,
    onClickGoToPremiumPage: func.isRequired,
    onPauseSubscription: func.isRequired,
    subscriptions: array,
  };

  static defaultProps = {
    subscriptions: [],
  };

  state = {
    isModalOpen: false,
    modalMode: 'choices',
    modalSubscription: null,
    selectedPauseOption: '',
  };

  onCloseModal = () => {
    this.setState({
      isModalOpen: false,
      modalSubscription: null,
      selectedPauseOption: '',
    });
  };

  onOpenModal = (mode, subscription) => {
    this.setState({
      isModalOpen: true,
      modalMode: mode,
      modalSubscription: subscription,
    });
  };

  onClickHelpAndCancel = ({ id, name, billingCycle }) => {
    const {
      canCancelSubscription,
      setCancelSubscriptionInfo,
      goToAccountSubscriptionCancelPage,
      setReportOptions,
      toggleReportForm,
    } = this.props;

    setReportOptions({ projectName: DRAGON_FRUIT_CANCEL });

    if (canCancelSubscription) {
      setCancelSubscriptionInfo({ id: `${id}`, name, billingCycle });
      goToAccountSubscriptionCancelPage(id);
    } else {
      toggleReportForm();
    }
  };

  getModalProps = () => {
    const { t } = this.props;
    const { modalMode, modalSubscription, selectedPauseOption } = this.state;

    const { expireAt, previewPausedResults } = modalSubscription;

    const onSelectPauseMonth = option => () =>
      this.setState({
        selectedPauseOption: option,
      });

    const renderPauseDescription = () => (
      <React.Fragment>
        <p
          dangerouslySetInnerHTML={{
            __html: t('account.pauseDescriptionModal.nextBillingHelpText', {
              date: yearMonthDayFormat(expireAt),
              interpolation: { escapeValue: false },
            }),
          }}
        />

        <div className={styles.pauseOptionWrapper}>
          <ul className={styles.pauseOptionList}>
            {previewPausedResults.map((pauseOption, pauseIndex) => (
              <li
                onClick={onSelectPauseMonth({
                  ...pauseOption,
                  billingCycles: pauseIndex + 1,
                })}
                className={cx(styles.pauseOption, {
                  selected:
                    selectedPauseOption.perMonths === pauseOption.perMonths,
                })}
                key={pauseOption.perMonths}
              >
                {t('account.pauseDescriptionModal.monthWithCount', {
                  count: pauseOption.perMonths,
                })}
              </li>
            ))}
          </ul>
          {selectedPauseOption && (
            <p
              className={styles.helpText}
              dangerouslySetInnerHTML={{
                __html: t('account.pauseDescriptionModal.continueOnHelpText', {
                  date: yearMonthDayFormat(selectedPauseOption.continueAt),
                  interpolation: {
                    escapeValue: false,
                  },
                }),
              }}
            />
          )}
        </div>

        <p>{t('account.pauseDescriptionModal.description')}</p>
      </React.Fragment>
    );

    const modalPropsMap = {
      choices: {
        centered: true,
        title: t('account.modifySubscriptionChoicesModal.title'),
        description: t('account.modifySubscriptionChoicesModal.description'),
        onClose: () => this.onCloseModal(),
        dismissButtonText: t('account.pauseDescriptionModal.dismissButtonText'),
        onDismiss: () => this.onCloseModal(),
        confirmButtonText: t(
          'account.modifySubscriptionChoicesModal.pauseButtonText'
        ),
        onConfirm: () => this.setState({ modalMode: 'pause' }),
      },
      pause: {
        title: t('account.pauseDescriptionModal.title'),
        description: renderPauseDescription(),
        dismissButtonText: t('account.pauseDescriptionModal.dismissButtonText'),
        onDismiss: () => this.onCloseModal(),
        confirmButtonText: t('account.pauseDescriptionModal.confirmButtonText'),
        onConfirm: () => this.handlePauseSubscription(),
        disabledConfirm: !selectedPauseOption,
      },
    };
    return modalPropsMap[modalMode];
  };

  handlePauseSubscription = () => {
    const { onPauseSubscription } = this.props;
    const {
      modalSubscription: { id: subscriptionId },
      selectedPauseOption: { billingCycles },
    } = this.state;

    if (!billingCycles) return;

    this.onCloseModal();
    onPauseSubscription({
      subscriptionId,
      billingCycles,
    });
  };

  renderSubscription = subscription => {
    const { t, toggleReportForm, onClickChangePlan } = this.props;

    const {
      id,
      name,
      billingCyclePerMonths,
      startAt,
      expireAt,
      paymentType,
      userCanPause: canPaused,
      continueAt,
      autoRenewal,
      subscriptionManagementStatus,
    } = subscription;

    // NOTE: Only the subscription is paid by Braintree can be paused or canceled on web.
    const isPayByBraintree = paymentType === BRAINTREE;

    const isManageable = subscriptionManagementStatus === MANAGEABLE;
    const isPaused = subscriptionManagementStatus === PAUSED;
    const isAnnual = subscriptionManagementStatus === CONTRACT_PLAN;
    const isRetired = subscriptionManagementStatus === RETIRED;
    const isReplacement = subscriptionManagementStatus === REPLACEMENT;
    const isCancelled = subscriptionManagementStatus === CANCELLED;
    const isAppliedForCancellation =
      subscriptionManagementStatus === APPLIED_FOR_CANCELLATION;
    const isPlanChanging = isRetired || isReplacement;
    const showMoreAnchor =
      isPayByBraintree &&
      autoRenewal &&
      !isPlanChanging &&
      !isPaused &&
      !isCancelled &&
      !isAppliedForCancellation;

    const onClickPause = event => {
      if (!isPayByBraintree || isPaused) return;
      event.preventDefault();
      this.onOpenModal('choices', subscription);
    };
    const onClickMoreAnchor = e => e.preventDefault();
    const renderRenewalDate = (createDate, renewalDate) =>
      isNeverExpired(createDate, renewalDate)
        ? t('account.planStatus.neverExpires')
        : t('account.planStatus.renewalOn', {
            date: yearMonthDayFormat(renewalDate),
            interpolation: {
              escapeValue: false,
            },
          });

    const renderBillingCycle = month => {
      if (month === 1) return t('account.planCircle.perMonth');
      if (month / 12 >= 1) return t('account.planCircle.perYear');
      return t('account.planCircle.perNumberMonth', { count: month });
    };

    const renderStatus = () => {
      if (isCancelled)
        return (
          <div className={styles.description}>
            <p
              dangerouslySetInnerHTML={{
                __html: t('account.planStatus.cancelled', {
                  date: yearMonthDayFormat(expireAt),
                }),
              }}
            />

            <p className={styles.helpText}>
              {t('account.planStatus.noAutoRenew')}
            </p>
          </div>
        );
      if (isAppliedForCancellation)
        return (
          <div className={styles.description}>
            <p
              dangerouslySetInnerHTML={{
                __html: t('account.cancelSentModal.title'),
              }}
            />

            <p className={styles.helpText}>
              {t('account.planStatus.processing')}
            </p>
          </div>
        );
      if (!autoRenewal)
        return (
          <div className={styles.description}>
            <p
              dangerouslySetInnerHTML={{
                __html: t('account.planStatus.expiresOn', {
                  date: yearMonthDayFormat(expireAt),
                }),
              }}
            />

            <p className={styles.helpText}>
              {t('account.planStatus.noAutoRenew')}
            </p>
          </div>
        );

      if (!isPayByBraintree)
        return (
          <div className={styles.description}>
            <p
              dangerouslySetInnerHTML={{
                __html: renderRenewalDate(startAt, expireAt),
              }}
            />
            <p className={styles.helpText}>
              {t(notAllowModifyOnWeb[paymentType])}
            </p>
          </div>
        );
      if (isRetired)
        return (
          <div className={styles.description}>
            <p
              dangerouslySetInnerHTML={{
                __html: t('account.planStatus.expiresOn', {
                  date: yearMonthDayFormat(expireAt),
                }),
              }}
            />
            <p
              className={styles.helpText}
              dangerouslySetInnerHTML={{
                __html: t('account.planStatus.cantManageAndWontRenew'),
              }}
            />
          </div>
        );

      if (isReplacement)
        return (
          <div className={styles.description}>
            <p
              dangerouslySetInnerHTML={{
                __html: renderRenewalDate(startAt, expireAt),
              }}
            />
            <p
              className={styles.helpText}
              dangerouslySetInnerHTML={{
                __html: t('account.planStatus.manageWhenStart'),
              }}
            />
          </div>
        );

      if (isAnnual)
        return (
          <div className={styles.description}>
            <p
              dangerouslySetInnerHTML={{
                __html: renderRenewalDate(startAt, expireAt),
              }}
            />
            <p
              className={styles.helpText}
              dangerouslySetInnerHTML={{
                __html: t('account.planStatus.notManageable'),
              }}
            />
          </div>
        );

      if (!isPaused)
        return (
          <div className={styles.description}>
            <p
              dangerouslySetInnerHTML={{
                __html: renderRenewalDate(startAt, expireAt),
              }}
            />
          </div>
        );

      return (
        <div className={styles.description}>
          <p
            dangerouslySetInnerHTML={{
              __html: t('account.planStatus.continueOn', {
                date: yearMonthDayFormat(continueAt),
                interpolation: {
                  escapeValue: false,
                },
              }),
            }}
          />
          <p className={styles.helpText}>
            <Trans i18nKey="account.planStatus.pausedDescription">
              If you wish to restart the plan, please
              <a onClick={toggleReportForm}>contact us</a>.
            </Trans>
          </p>
        </div>
      );
    };

    const billingCycle = renderBillingCycle(billingCyclePerMonths);

    return (
      <div className={styles.row} key={id}>
        <div data-label={t('account.currentPlan.plan')} className={styles.name}>
          {name}
        </div>
        <div
          data-label={t('account.currentPlan.circle')}
          className={styles.cycle}
        >
          {billingCycle}
        </div>
        <div
          data-label={t('account.currentPlan.status')}
          className={styles.status}
        >
          <div className={styles.content}>
            {renderStatus()}

            {showMoreAnchor && (
              // anchor here is for trigger the focus state and show moreList
              <a className={styles.more} href="#" onClick={onClickMoreAnchor}>
                <img src={MoreIcon} className={styles.icon} />
                <ul className={styles.moreList}>
                  {isManageable && (
                    <React.Fragment>
                      <li
                        className={styles.moreListItem}
                        onClick={onClickChangePlan('upgrade', id)}
                      >
                        {t('account.currentPlan.option.upgrade')}
                      </li>
                      <li
                        className={styles.moreListItem}
                        onClick={onClickChangePlan('downgrade', id)}
                      >
                        {t('account.currentPlan.option.downgrade')}
                      </li>
                      {canPaused && (
                        <li
                          className={styles.moreListItem}
                          onClick={onClickPause}
                        >
                          {t('account.currentPlan.option.pause')}
                        </li>
                      )}
                    </React.Fragment>
                  )}
                  <li
                    className={styles.moreListItem}
                    onClick={() =>
                      this.onClickHelpAndCancel({
                        id,
                        name,
                        billingCycle,
                      })
                    }
                  >
                    {t('account.currentPlan.option.helpAndCancel')}
                  </li>
                </ul>
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { t, subscriptions, onClickGoToPremiumPage } = this.props;

    const { isModalOpen, modalMode } = this.state;

    return (
      <div className={styles.root}>
        {subscriptions.length ? (
          <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
              <div className={styles.name}>{t('account.currentPlan.plan')}</div>
              <div className={styles.cycle}>
                {t('account.currentPlan.circle')}
              </div>
              <div className={styles.status}>
                {t('account.currentPlan.status')}
              </div>
            </div>
            <div className={styles.tableBody}>
              {subscriptions.map(this.renderSubscription)}
              {isModalOpen && (
                <ChangeSubscriptionModal {...this.getModalProps(modalMode)} />
              )}
            </div>
          </div>
        ) : (
          <div className={styles.emptyStatus}>
            <img
              className={styles.illustration}
              src={t('account.emptyStateImg')}
              alt={t('account.noActiveSubscriptions')}
            />
            <p className={styles.description}>
              {t('account.noActiveSubscriptions')}
            </p>
            <Button
              onClick={onClickGoToPremiumPage}
              text={t('account.seePackages')}
              primary
            />
          </div>
        )}
      </div>
    );
  }
}

AccountCurrentSubscriptions.contextTypes = {
  urlLocale: string,
};

export default translate(['account'])(
  connect(mapStateToProps, {
    setCancelSubscriptionInfo,
    goToAccountSubscriptionCancelPage,
  })(AccountCurrentSubscriptions)
);



// WEBPACK FOOTER //
// ./app/containers/Account/children/AccountSubscriptions/components/AccountCurrentSubscriptions/AccountCurrentSubscriptions.jsx